import { useQueryParamTerm } from '../../utils/urls/appQueryParamUtils';
import { useHighestProductLevelFromInventory } from '../inventory/useHighestProductLevel';
import { useProductInventoryQuantityByApiName } from '../inventory/useProductInventoryQuantity';
import { useDisplayedCurrencyCode } from '../pricing/useCurrencyDetails';
import { useGrowthPopForPurchaseMotions } from '../pricing/useGrowthPurchaseOptions';
import { useProductDetails } from '../product/useProductDetails';
export const usePurchaseRequestForPurchaseMotion = ({
  productApiName,
  purchaseMotion,
  skipInterstitial,
  skip
}) => {
  const displayedCurrencyCode = useDisplayedCurrencyCode();
  const {
    queryParamTerm
  } = useQueryParamTerm();
  const {
    commitmentTerm,
    paymentTerm
  } = useGrowthPopForPurchaseMotions({
    productApiName: skip ? undefined : productApiName,
    // omitting productApiName will effectively skip the hook query
    term: queryParamTerm
  });

  // fetch the product details for the parent or main product
  const {
    loading,
    productDetails
  } = useProductDetails({
    productApiName: skip ? undefined : productApiName
  });

  // fetch the owned quantity for the parent or main product
  const {
    quantity: ownedQuantity
  } = useProductInventoryQuantityByApiName({
    apiName: skip ? undefined : productApiName
  });
  const {
    highestProductLevel
  } = useHighestProductLevelFromInventory({
    skip
  });
  if (skip || loading || !productDetails) {
    return {
      products: [],
      quantityPacks: [],
      commitmentTerm: commitmentTerm !== null && commitmentTerm !== void 0 ? commitmentTerm : undefined,
      paymentTerm: paymentTerm !== null && paymentTerm !== void 0 ? paymentTerm : undefined,
      currencyCode: displayedCurrencyCode !== null && displayedCurrencyCode !== void 0 ? displayedCurrencyCode : 'USD'
    };
  }
  const {
    quantifiable,
    quantityPacks
  } = productDetails;
  const requestedProducts = [];
  const requestedQuantities = [];
  switch (purchaseMotion) {
    case 'BUY_TOUCHLESS':
      {
        // first time purchases (any product: hubs, suite, add-ons, credits, etc.)
        // subsequent purchases of add-ons or credits will also fall into this case
        // because they are quantifiable
        if (ownedQuantity === 0 || quantifiable) {
          // if the user doesn't own the product, the incremental quantity will be 1
          const doesntOwnProduct = ownedQuantity === 0;

          // if the user DOES own the product, the incremental quantity will be 0 because
          // they will likley be going to interstitial to increase the quantity there, but we don't
          // want to increment it before they get there.

          // if the user just skips the interstitial, we always add 1, and they can change it in checkout if they want.

          requestedProducts.push({
            productApiName,
            incrementalQuantity: skipInterstitial || doesntOwnProduct ? 1 : 0
          });
        }
        break;
      }
    case 'BUY_ADDITIONAL_CONTACTS':
      {
        // contacts are ALWAYS quantity packs, so lets find the quantity pack for contacts
        // from the parent product
        const quantityPack = quantityPacks.find(qp => qp.quantityName === 'CONTACTS');
        if (quantityPack && quantityPack.quantityName) {
          requestedQuantities.push({
            parentProductApiName: productApiName,
            quantityName: quantityPack.quantityName,
            incrementalQuantity: skipInterstitial ? 1 : 0
          });
        }
        break;
      }
    case 'BUY_ADDITIONAL_SEATS':
      {
        // quantfiable hubs - if you own multiple persona seats, we will only add
        // the one from the productApiName passed in (e.g. the given merch card you are on)
        if (quantifiable) {
          // if the user doesn't own the product, the incremental quantity will be 1
          const doesntOwnProduct = ownedQuantity === 0;

          // if the user DOES own the product, the incremental quantity will be 0 because
          // they will likley be going to interstitial to increase the quantity there, but we don't
          // want to increment it before they get there.

          // if the user just skips the interstitial, we always add 1, and they can change it in checkout if they want.

          requestedProducts.push({
            productApiName,
            incrementalQuantity: skipInterstitial || doesntOwnProduct ? 1 : 0
          });
        }

        // non-quantifiable hubs - single hub products with standalone core seats or suites
        if (!quantifiable) {
          // construct a core seats api name for the highest product level knowing that
          // core seats always align with the highest owned product level or edition
          const coreSeatsApiName = `core-seats-${highestProductLevel}`.toLowerCase();

          // always add the core seats, because if a product is not quantifiable
          // it will always have a core seat if the purchase motion is BUY_ADDITIONAL_SEATS
          requestedProducts.push({
            productApiName: coreSeatsApiName,
            incrementalQuantity: skipInterstitial ? 1 : 0
          });

          // if the parent product is a suite, we will have seats as quantity packs
          if ((quantityPacks === null || quantityPacks === void 0 ? void 0 : quantityPacks.length) > 0) {
            const seatsQuantityPacks = quantityPacks.filter(qp => qp.quantityName === 'SALES_SEAT' || qp.quantityName === 'SERVICE_SEAT' || qp.quantityName === 'COMMERCE_SEAT');
            seatsQuantityPacks.forEach(qp => {
              if (qp && qp.quantityName) {
                requestedQuantities.push({
                  parentProductApiName: productApiName,
                  quantityName: qp.quantityName,
                  incrementalQuantity: 0
                });
              }
            });
          }
        }
        break;
      }
    default:
      break;
  }
  return {
    products: requestedProducts,
    quantityPacks: requestedQuantities,
    commitmentTerm: commitmentTerm !== null && commitmentTerm !== void 0 ? commitmentTerm : undefined,
    paymentTerm: paymentTerm !== null && paymentTerm !== void 0 ? paymentTerm : undefined,
    currencyCode: displayedCurrencyCode !== null && displayedCurrencyCode !== void 0 ? displayedCurrencyCode : 'USD'
  };
};